import * as z from 'zod'

import { sanitizeEmail, sanitizePhoneNumber } from '@web-app/utils/sanitize'
import { validateEmail, validatePhone } from '@web-app/utils/validation'

export type ClassifiedIdentifier =
  | { kind: 'email'; value: string }
  | { kind: 'phone'; value: string }

// An "@" anywhere means email; otherwise a 10-digit US phone.
export function classifyIdentifier({
  raw,
}: {
  raw: string
}): ClassifiedIdentifier | undefined {
  const trimmed = raw.trim()
  if (!trimmed) return undefined
  if (trimmed.includes('@')) {
    const email = sanitizeEmail(trimmed)
    return validateEmail(email) ? { kind: 'email', value: email } : undefined
  }
  const digits = sanitizePhoneNumber(trimmed)
  return digits.length === 10 ? { kind: 'phone', value: digits } : undefined
}

// One issue at a time: an empty field is "missing", anything else is "invalid".
export const identifierSchema = z.object({
  identifier: z.string().superRefine((raw, ctx) => {
    if (!raw.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter your email or mobile number',
      })
    } else if (!classifyIdentifier({ raw })) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter a valid email or 10-digit mobile number',
      })
    }
  }),
})
export type IdentifierValues = z.infer<typeof identifierSchema>

// Stricter than the backend's 8+ floor on purpose.
export const PASSWORD_RULE = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{12,}$/
export const PASSWORD_RULE_HINT =
  'At least 12 characters, with an uppercase letter, a lowercase letter, and a number'

export const createAccountSchema = z
  .object({
    phone: z.string().refine(validatePhone, {
      message: 'Enter a 10-digit US mobile number',
    }),
    email: z.string().trim().refine(validateEmail, {
      message: 'Enter a valid email address',
    }),
    password: z.string().regex(PASSWORD_RULE, PASSWORD_RULE_HINT),
    confirmPassword: z.string(),
  })
  .superRefine((values, ctx) => {
    if (values.confirmPassword !== values.password) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['confirmPassword'],
        message: 'Passwords do not match',
      })
    }
  })
export type CreateAccountValues = z.infer<typeof createAccountSchema>

export const loginPasswordSchema = z.object({
  password: z.string().min(1, 'Enter your password'),
})
export type LoginPasswordValues = z.infer<typeof loginPasswordSchema>

export const phoneSchema = z.object({
  phone: z.string().refine(validatePhone, {
    message: 'Enter a 10-digit US mobile number',
  }),
})
export type PhoneValues = z.infer<typeof phoneSchema>

export const otpCodeSchema = z.object({
  code: z.string().length(6, 'Enter the 6-digit code'),
})
export type OtpCodeValues = z.infer<typeof otpCodeSchema>
